import { useMemo } from 'react';
import { format, startOfMonth, parseISO, addMonths } from 'date-fns';
import { useSpendingByCategory, useSpendingByCategoryOverTime as useSpendingByCategoryOverTimeQuery } from '../../../hooks/useTransactions';
import {
  computeCategoryMonthlyAverages,
  getMonthlyChartPeriodKeys,
  shouldAppendProjectedMonth,
  shouldMarkPeriodAsProjected,
} from '../../../utils/spending-trends-chart';
import type { SpendingByCategoryParams, SpendingByCategoryResponse } from '../../../types/transaction.types';

export interface DashboardSpendingCategory {
  categoryId: string | null;
  name: string;
  slug?: string;
  color: string;
  icon?: string;
  amount: number;
  percentage: number;
  type: 'income' | 'expense' | 'transfer';
  relatedCategoryIds?: string[];
  isExcluded?: boolean;
}

export interface UseDashboardSpendingOptions {
  userId: string;
  dateFrom?: string;
  dateTo?: string;
  grouping?: 'parent' | 'category';
  includeExcluded?: boolean;
}

const DEFAULT_COLORS = [
  '#3B82F6',
  '#8B5CF6',
  '#F97316',
  '#10B981',
  '#EC4899',
  '#F59E0B',
  '#0EA5E9',
  '#6366F1',
];

const FALLBACK_COLOR = '#6B7280';

function formatDateIso(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

function computePercentages(response?: SpendingByCategoryResponse): DashboardSpendingCategory[] {
  if (!response || response.items.length === 0) {
    return [];
  }

  const { items, total } = response;
  const safeTotal = total > 0 ? total : items.reduce((sum, item) => sum + item.totalAmount, 0);

  return items.map((item, index) => {
    const color =
      item.categoryColor ||
      DEFAULT_COLORS[index % DEFAULT_COLORS.length] ||
      FALLBACK_COLOR;

    return {
      categoryId: item.categoryId,
      name: item.categoryName,
      slug: item.categorySlug,
      color,
      icon: item.categoryIcon,
      amount: item.totalAmount,
      percentage: safeTotal > 0 ? (item.totalAmount / safeTotal) * 100 : 0,
      type: item.type,
      relatedCategoryIds: item.relatedCategoryIds,
      isExcluded: item.isExcluded,
    };
  });
}

export function useDashboardSpendingByCategory(options: UseDashboardSpendingOptions) {
  const { userId, dateFrom, dateTo } = options;
  const grouping = options.grouping ?? 'parent';
  const includeExcluded = options.includeExcluded ?? false;

  const params: SpendingByCategoryParams = {
    userId,
    dateFrom: dateFrom ?? formatDateIso(startOfMonth(new Date())),
    dateTo: dateTo ?? formatDateIso(new Date()),
    grouping,
    includeExcluded,
  };

  const query = useSpendingByCategory(params);

  const categories = useMemo(
    () => {
      const computed = computePercentages(query.data);
      // Sort by amount descending (highest to lowest)
      return computed.sort((a, b) => b.amount - a.amount);
    },
    [query.data]
  );

  const total = useMemo(() => {
    return categories.reduce((sum, category) => sum + category.amount, 0);
  }, [categories]);

  return {
    ...query,
    categories,
    total,
    transactionCount: query.data?.transactionCount,
  };
}

/**
 * Hook to get spending by category over time for trending charts
 * Transforms the data into a format suitable for recharts LineChart
 */
export function useSpendingByCategoryOverTime(options: UseDashboardSpendingOptions) {
  const { userId, dateFrom, dateTo } = options;
  const grouping = options.grouping ?? 'parent';
  const includeExcluded = options.includeExcluded ?? false;

  const params: SpendingByCategoryParams = {
    userId,
    dateFrom: dateFrom ?? formatDateIso(startOfMonth(new Date())),
    dateTo: dateTo ?? formatDateIso(new Date()),
    grouping,
    includeExcluded,
  };

  const query = useSpendingByCategoryOverTimeQuery(params);

  // Transform data for recharts LineChart
  const chartData = useMemo(() => {
    if (!query.data || query.data.items.length === 0) {
      return [];
    }

    const { items, granularity, topCategories } = query.data;

    // Group items by period
    const periodMap = new Map<string, Record<string, number>>();

    for (const item of items) {
      if (!periodMap.has(item.period)) {
        periodMap.set(item.period, {});
      }
      const periodData = periodMap.get(item.period)!;
      const categoryKey = item.categoryId ?? 'uncategorised';
      periodData[categoryKey] = item.amount;
    }

    const now = new Date();
    const currentMonthKey = format(startOfMonth(now), 'yyyy-MM');

    // Historical ranges include every month; active ranges stop at the current month.
    const sortedPeriods =
      granularity === 'monthly' && dateFrom && dateTo
        ? getMonthlyChartPeriodKeys(dateFrom, dateTo, now)
        : Array.from(periodMap.keys()).sort();

    const categoryKeys = topCategories.map((category) => category.categoryId ?? 'uncategorised');
    const completedPeriods = sortedPeriods.filter((period) => period !== currentMonthKey);
    const categoryAverages = computeCategoryMonthlyAverages(periodMap, completedPeriods, categoryKeys);

    const chartDataArray = sortedPeriods.map((period) => {
      const periodData = periodMap.get(period) ?? {};
      const isProjected = shouldMarkPeriodAsProjected(period, granularity, now);

      const dataPoint: Record<string, string | number | boolean> = {
        period: formatPeriodLabel(period, granularity),
        periodKey: period,
        isProjected,
      };

      for (const category of topCategories) {
        const categoryKey = category.categoryId ?? 'uncategorised';
        dataPoint[categoryKey] = isProjected
          ? categoryAverages.get(categoryKey) || 0
          : periodData[categoryKey] || 0;
      }

      return dataPoint;
    });

    // Append exactly one projected month ahead when viewing an active range.
    if (granularity === 'monthly' && chartDataArray.length > 0 && dateTo) {
      const lastPeriod = sortedPeriods[sortedPeriods.length - 1];

      try {
        const nextPeriodKey = format(addMonths(parseISO(`${lastPeriod}-01`), 1), 'yyyy-MM');
        const hasNextMonth = sortedPeriods.includes(nextPeriodKey);

        if (!hasNextMonth && shouldAppendProjectedMonth(nextPeriodKey, dateTo, now)) {
          const nextMonthPoint: Record<string, string | number | boolean> = {
            period: format(parseISO(`${nextPeriodKey}-01`), 'MMM yyyy'),
            periodKey: nextPeriodKey,
            isProjected: true,
          };

          for (const categoryKey of categoryKeys) {
            nextMonthPoint[categoryKey] = categoryAverages.get(categoryKey) || 0;
          }

          chartDataArray.push(nextMonthPoint);
        }
      } catch {
        return chartDataArray;
      }
    }

    return chartDataArray;
  }, [query.data, dateFrom, dateTo]);

  return {
    ...query,
    chartData,
    topCategories: query.data?.topCategories ?? [],
    granularity: query.data?.granularity ?? 'daily',
    transactionCount: query.data?.transactionCount,
  };
}

function formatPeriodLabel(period: string, granularity: 'daily' | 'weekly' | 'monthly'): string {
  try {
    if (granularity === 'daily') {
      // Format: MM/DD
      const date = parseISO(period);
      return format(date, 'MM/dd');
    } else if (granularity === 'weekly') {
      // Format: Week of MM/DD
      const date = parseISO(period);
      return format(date, 'MM/dd');
    } else {
      // Format: MMM YYYY
      const date = parseISO(period + '-01');
      return format(date, 'MMM yyyy');
    }
  } catch {
    return period;
  }
}

